import React from 'react';
import { Box, Typography, Avatar, alpha, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { StyledCard, StyledCardHeader, StyledCardContent, MetricChip, formatDisplayItem } from '@features/analysis/components/StyledComponents';
import { PropertyAssessmentCardProps } from '@features/analysis/types';

const PropertyAssessmentCard: React.FC<PropertyAssessmentCardProps> = ({ analysis }) => {
  const theme = useTheme();

  return (
    <StyledCard>
      <StyledCardHeader
        title="Property Assessment"
        subheader="Evaluation of property characteristics"
        avatar={
          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
            <BusinessIcon />
          </Avatar>
        }
      />
      <StyledCardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Strengths
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {analysis.propertyAssessment?.strengths && analysis.propertyAssessment.strengths.length > 0 ? (
                analysis.propertyAssessment.strengths.map((strength, index) => (
                  <MetricChip
                    key={`${index}-strength`}
                    label={formatDisplayItem(strength)}
                    color="success"
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No strengths identified.
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Areas for Improvement
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {analysis.propertyAssessment?.weaknesses && analysis.propertyAssessment.weaknesses.length > 0 ? (
                analysis.propertyAssessment.weaknesses.map((weakness, index) => (
                  <MetricChip
                    key={`${index}-weakness`}
                    label={formatDisplayItem(weakness)}
                    color="warning"
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No weaknesses identified.
                </Typography>
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Strategic Recommendations
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {analysis.propertyAssessment?.recommendations && analysis.propertyAssessment.recommendations.length > 0 ? (
                analysis.propertyAssessment.recommendations.map((recommendation, index) => (
                  <MetricChip
                    key={`${index}-recommendation`}
                    label={formatDisplayItem(recommendation)}
                    color="primary"
                    variant="filled"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recommendations available.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </StyledCardContent>
    </StyledCard>
  );
};

export default PropertyAssessmentCard;